/**
 * Compile a free-form filter expression into a per-row predicate.
 *
 * Syntax goals (kept close to what a business user might type in Excel /
 * pandas):
 *   - Reference columns by their lowercase name (e.g. `total_sales`)
 *   - Operators: `==`, `!=`, `<`, `<=`, `>`, `>=`, `+ - * / %`
 *   - Logical: `and` / `or` / `not`  (and `&&` / `||` / `!` work too)
 *   - String literals in single or double quotes
 *   - `in`, `between` aliases: `region in ('east','west')`,
 *     `total_sales between 50000 and 150000`
 *   - `contains` / `like`: case-insensitive substring test against a quoted
 *     string, e.g. `customer_name contains 'corp'`
 *
 * Safety: we compile to a JS function using `new Function` but bind only
 * the row columns as locals (no access to globals, no `this`). The
 * expression cannot reach window/document/etc -- the function body opens
 * with `"use strict"` and the body has no other names in scope. This is
 * adequate for a local-dev tool; do not expose this to untrusted users.
 */

const KEYWORD_RX = /\b(and|or|not)\b/g;
const KEYWORD_MAP: Record<string, string> = { and: '&&', or: '||', not: '!' };

function escapeColName(c: string): string {
  // Column names are already validated alpha/num/underscore in our pipeline;
  // be defensive anyway.
  return c.replace(/[^A-Za-z0-9_]/g, '_').toLowerCase();
}

/**
 * Rewrite `region in ('a','b')` -> `(region == 'a' || region == 'b')`.
 * Rewrite `x between A and B` -> `(x >= A && x <= B)`.
 */
function rewriteSugar(expr: string, columns: string[]): string {
  let out = expr;

  // CONTAINS / LIKE -> case-insensitive substring test via the __contains
  // helper that we inject into the function body. The value must be a quoted
  // string literal.
  const containsRx = /\b([A-Za-z_][A-Za-z0-9_]*)\s+(?:contains|like)\s+('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/gi;
  out = out.replace(containsRx, (_, col, lit) => `__contains(${col}, ${lit})`);

  // BETWEEN ... AND ...  (only when AND is the 'between' continuation, not a
  // separate boolean clause). We use a tight regex that consumes both
  // bounds as either numbers, quoted strings, or identifiers.
  const betweenRx = /\b([A-Za-z_][A-Za-z0-9_]*)\s+between\s+([^()]+?)\s+and\s+([^()]+?)(?=$|\)|\s+(?:and|or|&&|\|\|)\b)/gi;
  out = out.replace(betweenRx, (_, col, lo, hi) => `(${col} >= ${lo.trim()} && ${col} <= ${hi.trim()})`);

  // IN (...)
  const inRx = /\b([A-Za-z_][A-Za-z0-9_]*)\s+in\s*\(([^()]+)\)/gi;
  out = out.replace(inRx, (_, col, list) => {
    const items = list.split(',').map((s: string) => s.trim()).filter(Boolean);
    if (!items.length) return 'false';
    return '(' + items.map((it: string) => `${col} == ${it}`).join(' || ') + ')';
  });

  // Logical word operators.
  out = out.replace(KEYWORD_RX, (m) => KEYWORD_MAP[m.toLowerCase()] || m);
  return out;
}

export interface CompiledFilter {
  predicate: (row: Record<string, unknown>) => boolean;
  warning?: string; // compile-time warning (e.g. unknown column)
}

export class FilterCompileError extends Error {}

export function compileFilter(expr: string, columns: string[]): CompiledFilter {
  const trimmed = expr.trim();
  if (!trimmed) {
    return { predicate: () => true };
  }
  const lcCols = columns.map((c) => c.toLowerCase());
  const rewritten = rewriteSugar(trimmed, lcCols);

  // Surface a hint if the user referenced an identifier that isn't a column.
  // Blank out string literals first so their contents (e.g. 'east') aren't
  // mistaken for unknown column references.
  const idents = new Set<string>();
  const noStrings = rewritten.replace(/'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g, '');
  noStrings.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (m) => { idents.add(m.toLowerCase()); return m; });
  const reserved = new Set([
    'true', 'false', 'null', 'undefined', 'in', 'between',
    'and', 'or', 'not', 'contains', 'like', '__contains',
  ]);
  const unknown = [...idents].filter((id) => !lcCols.includes(id) && !reserved.has(id));
  const warning = unknown.length
    ? `Unknown name(s): ${unknown.slice(0, 5).join(', ')}${unknown.length > 5 ? '…' : ''}`
    : undefined;

  // Build the function body. Each known column is bound from row.
  // We coerce strings that look numeric to numbers so '241077.48' compares
  // correctly against numeric literals.
  const declarations = lcCols.map((c) => {
    const safe = escapeColName(c);
    return `var ${safe} = row[${JSON.stringify(c)}];` +
           ` if (typeof ${safe} === 'string' && ${safe} !== '' && !isNaN(Number(${safe}))) ${safe} = Number(${safe});`;
  }).join('\n');

  // Helper(s) referenced by the rewritten expression (e.g. `contains`).
  const helpers = 'function __contains(a, b) {'
    + ' if (a === null || a === undefined) return false;'
    + ' return String(a).toLowerCase().indexOf(String(b).toLowerCase()) !== -1; }';

  const body = `"use strict";\n${helpers}\n${declarations}\ntry { return Boolean(${rewritten}); } catch (e) { return false; }`;

  let fn: (row: Record<string, unknown>) => boolean;
  try {
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    fn = new Function('row', body) as (r: Record<string, unknown>) => boolean;
  } catch (e) {
    throw new FilterCompileError((e as Error).message);
  }
  return { predicate: fn, warning };
}
